import { showToast } from "@certn/cascadia";
import {
  BasicButton,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Modal,
  PrimaryButton,
  SectionMessage,
  Stack,
  Text,
} from "@certn/cascadia-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { EligibleMonitoringCheck } from "../types";
import { toggleSetItem } from "../types";
import { useCreateEnrollments } from "../useMonitoringApi";
import { checkRowSpaceBetweenStyle } from "@certn/monitoring";

interface Props {
  checks: EligibleMonitoringCheck[];
  onClose: () => void;
  onSuccess?: () => void | Promise<void>;
}

export function EnrollCaseMonitoringModal({
  checks,
  onClose,
  onSuccess,
}: Props) {
  const { t } = useTranslation();
  const [selectedCheckIds, setSelectedCheckIds] = useState<Set<string>>(
    () => new Set(checks.map((check) => check.id))
  );
  const checkIdsKey = checks
    .map((check) => check.id)
    .sort()
    .join(",");

  useEffect(() => {
    const checkIds = checkIdsKey === "" ? [] : checkIdsKey.split(",");
    setSelectedCheckIds(new Set(checkIds));
  }, [checkIdsKey]);

  const { submit, isSubmitting } = useCreateEnrollments({
    onSuccess: async () => {
      void Promise.resolve(onSuccess?.()).catch(() => {});
      showToast(t("Monitoring started."), { type: "success" });
      onClose();
    },
  });

  function toggleCheck(checkId: string) {
    setSelectedCheckIds((prev) => toggleSetItem(prev, checkId));
  }

  async function handleConfirm() {
    if (selectedCheckIds.size === 0) return;
    await submit({ check_ids: Array.from(selectedCheckIds) });
  }

  return (
    <Modal isOpen testID="enroll-case-monitoring-modal">
      <Dialog>
        <DialogHeader onClose={onClose}>
          {t("Select checks for monitoring")}
        </DialogHeader>
        <DialogContent>
          <Stack gap="space-4">
            <SectionMessage
              type="warning"
              icon="warning"
              testID="enroll-case-cost-warning"
            >
              {t(
                "Please note, this action will enrol the applicant in continuous monitoring and will incur a subscription charge per applicant for each of the selected check."
              )}
            </SectionMessage>
            <Stack gap="space-0">
              {checks.map((check) => (
                <div key={check.id} className={checkRowSpaceBetweenStyle}>
                  <Text style="global/body-primary">{check.name}</Text>
                  <Checkbox
                    isSelected={selectedCheckIds.has(check.id)}
                    onChange={() => toggleCheck(check.id)}
                    aria-label={check.name}
                    testID={`enroll-case-check-${check.id}`}
                  >
                    {""}
                  </Checkbox>
                </div>
              ))}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogFooter>
          <BasicButton onPress={onClose} testID="enroll-case-cancel-button">
            {t("Cancel")}
          </BasicButton>
          <PrimaryButton
            isDisabled={selectedCheckIds.size === 0 || isSubmitting}
            onPress={handleConfirm}
            testID="enroll-case-confirm-button"
          >
            {isSubmitting ? t("Starting…") : t("Start monitoring")}
          </PrimaryButton>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}
